import { useRef } from 'react';
import { services } from '../../../shared/config/site';
import { useScrollChoreography } from '../../../shared/hooks/useScrollChoreography';
import { motionQuery } from '../../../shared/lib/gsap';
import { PerimeterTile } from './PerimeterTile';

/**
 * The card grid's replacement: hairline index rows.
 *
 * Each row carries its own drawn plan tile, so the reader gets six different
 * pictures of the work rather than six padded boxes of the same shape.
 */
export function ServiceIndex() {
  const rootRef = useRef<HTMLElement>(null);

  useScrollChoreography(rootRef, ({ gsap }) => {
    const media = gsap.matchMedia();

    media.add(motionQuery.full, () => {
      gsap.utils.toArray<HTMLElement>('[data-service-row]').forEach((row, index) => {
        gsap.fromTo(
          row,
          { y: 16, opacity: 0.001 },
          {
            y: 0,
            opacity: 1,
            // An entrance, not a performance: 280ms and 16px of travel.
            duration: 0.28,
            ease: 'power2.out',
            delay: (index % 3) * 0.04,
            scrollTrigger: { trigger: row, start: 'top 88%', once: true },
            onComplete: () => row.classList.add('is-in'),
          },
        );
      });
    });

    return () => media.revert();
  });

  return (
    <section ref={rootRef} id="services" className="u-band bg-[color:var(--color-surface)]">
      <div className="u-shell">
        <header className="mb-14 flex flex-col gap-5 sm:mb-20 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="u-balance u-display max-w-[16ch] text-[clamp(1.75rem,3.6vw,3rem)] text-[color:var(--bone-50)]">
            Six things we are actually called for
          </h2>
          <p className="u-label shrink-0 text-[color:var(--steel-500)]">Index / 01–06</p>
        </header>

        <ul className="flex flex-col">
          {services.map((service) => (
            <li key={service.slug} data-service-row className="u-reveal u-rule">
              <div className="grid grid-cols-[3.25rem_1fr] items-start gap-x-5 gap-y-6 py-9 sm:grid-cols-[4.5rem_1fr_9rem] sm:gap-x-8 sm:py-12 lg:grid-cols-[5rem_1fr_1fr_11rem]">
                <span className="u-label pt-2 text-[color:var(--signal-500)]">{service.index}</span>

                <h3 className="u-display text-[clamp(1.6rem,4vw,2.6rem)] text-[color:var(--bone-50)]">
                  {service.title}
                </h3>

                <p className="col-span-2 max-w-[40ch] text-[color:var(--steel-400)] sm:col-span-1 sm:col-start-2 lg:col-span-1 lg:col-start-3 lg:pt-2">
                  {service.summary}
                </p>

                <div className="col-span-2 h-[7rem] w-[7rem] sm:col-span-1 sm:col-start-3 sm:row-start-1 sm:h-[9rem] sm:w-[9rem] lg:col-start-4 lg:h-[11rem] lg:w-[11rem]">
                  <PerimeterTile plan={service.plan} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
